/**
 * LICITARS 3.0 - Document Info Modal
 * Modal to display detailed document and process information
 * Similar to Chat8MessagesModal, but focused on document data
 */

import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Calendar, User, Hash, Clock, Info } from "lucide-react";
import { ProcessDetailsAccordionV3 } from "./ProcessDetailsAccordionV3";

interface DocumentInfoModalProps {
  document: any;
  triggerButton?: React.ReactNode;
}

/**
 * DocumentInfoModal - Modal to display document and process data
 * Replaces the accordion with a more intuitive modal interface
 */
export const DocumentInfoModal: React.FC<DocumentInfoModalProps> = React.memo(({
  document,
  triggerButton
}) => {
  // If there is no document, don't render
  if (!document) return null;
  // Default button if not provided
  const defaultTriggerButton = useMemo(() => (
    <Button
      variant="outline"
      size="sm"
      className="flex items-center gap-2 bg-navy-700 border-navy text-platinum hover:bg-navy-600"
    >
      <Info className="h-4 w-4" />
      <span>Informações do Documento</span>
    </Button>
  ), []);

  return (
    <Dialog>
      <DialogTrigger asChild>
        {triggerButton || defaultTriggerButton}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col bg-navy-800 border-navy z-modal">
        <DialogHeader className="flex-shrink-0 pb-4 border-b border-navy bg-navy-700/50">
          <DialogTitle className="text-platinum font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            Dados do Documento e Processo
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-blue-400" />
              <h3 className="text-base font-bold text-platinum uppercase tracking-wider">
                Dados do Documento
              </h3>
            </div>

            <dl className="grid grid-cols-1 gap-4 text-sm">
              <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                <dt className="text-xs font-semibold text-silver/70 uppercase tracking-wide">
                  Nome:
                </dt>
                <dd className="text-platinum font-medium text-base">
                  {document.name || document.title}
                </dd>
              </div>

              {document.description && (
                <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                  <dt className="text-xs font-semibold text-silver/70 uppercase tracking-wide">
                    Descrição:
                  </dt>
                  <dd className="text-platinum">{document.description}</dd>
                </div>
              )}

              {document.document_type && (
                <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                  <dt className="text-xs font-semibold text-silver/70 uppercase tracking-wide">
                    Tipo:
                  </dt>
                  <dd className="mt-2 text-platinum">
                    <span className="px-3 py-1.5 rounded-md bg-blue-500/20 text-blue-300 text-sm font-medium">
                      {document.document_type.name}
                    </span>
                  </dd>
                </div>
              )}

              <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1 uppercase tracking-wide">
                  <Hash className="w-3.5 h-3.5" />
                  ID do Documento:
                </dt>
                <dd className="text-silver/90 font-mono text-xs bg-navy-900/50 px-3 py-2 rounded border border-navy-600 break-all">
                  {document.id}
                </dd>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {document.created_at && (
                  <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                    <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1 uppercase tracking-wide">
                      <Calendar className="w-3.5 h-3.5" />
                      Criado em:
                    </dt>
                    <dd className="text-platinum text-sm">
                      {new Date(document.created_at).toLocaleString("pt-BR", {
                        dateStyle: "short",
                        timeStyle: "short"
                      })}
                    </dd>
                  </div>
                )}

                {document.updated_at && (
                  <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                    <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1 uppercase tracking-wide">
                      <Clock className="w-3.5 h-3.5" />
                      Atualizado em:
                    </dt>
                    <dd className="text-platinum text-sm">
                      {new Date(document.updated_at).toLocaleString("pt-BR", {
                        dateStyle: "short",
                        timeStyle: "short"
                      })}
                    </dd>
                  </div>
                )}
              </div>

              {document.user && (
                <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                  <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1 uppercase tracking-wide">
                    <User className="w-3.5 h-3.5" />
                    Usuário Responsável:
                  </dt>
                  <dd className="text-platinum">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{document.user.username}</span>
                      {document.user.email && (
                        <span className="text-sm text-silver/60">
                          ({document.user.email})
                        </span>
                      )}
                    </div>
                  </dd>
                </div>
              )}

              {document.session_id && (
                <div className="flex flex-col gap-1 p-3 bg-navy-700/30 rounded-lg">
                  <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1 uppercase tracking-wide">
                    <Hash className="w-3.5 h-3.5" />
                    Session ID:
                  </dt>
                  <dd className="text-silver/90 font-mono text-xs bg-navy-900/50 px-3 py-2 rounded border border-navy-600 break-all">
                    {document.session_id}
                  </dd>
                </div>
              )}
            </dl>
          </section>
          {/* Section: Process Data */}
          {document.process && (
            <>
              <div className="h-px bg-white/10 my-4" />
              <section className="space-y-4">
                <ProcessDetailsAccordionV3
                  process={document.process}
                />
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

DocumentInfoModal.displayName = 'DocumentInfoModal';
